import React, { useState, useRef, useEffect } from 'react';
import '../../types/global'; // Import global type declarations

// Define LoginFormProps interface
interface LoginFormProps {
  onSubmit: (email: string, captchaToken: string) => Promise<void>;
  isLoading: boolean;
}

const TypewriterDemo: React.FC = () => {
  const [currentText, setCurrentText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTyping, setIsTyping] = useState(true);

  const questions = [
    "How can I get my wife more in to me?",
    "My wife is totally uninterested in sex, should I get a divorce?",
    "I do everything my wife asks me to do but she never wants to have sex, why?"
  ];

  useEffect(() => {
    if (!isTyping) {
      const timeout = setTimeout(() => {
        setCurrentText('');
        setCurrentIndex((prevIndex) => (prevIndex + 1) % questions.length);
        setIsTyping(true);
      }, 2000);
      return () => clearTimeout(timeout);
    }

    if (currentText.length < questions[currentIndex].length) {
      const timeout = setTimeout(() => {
        setCurrentText(questions[currentIndex].slice(0, currentText.length + 1));
      }, 50);
      return () => clearTimeout(timeout);
    } else {
      setIsTyping(false);
    }
  }, [currentText, currentIndex, isTyping, questions]);

  return (
    <div className="w-full">
      <div className="bg-slate-800/70 backdrop-blur border border-slate-700 rounded-lg p-4">
        <div className="flex items-center gap-2 border-b border-slate-700/50 pb-2 mb-3">
          <div className="flex gap-1.5">
            <div className="w-2.5 h-2.5 rounded-full bg-red-500/80"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80"></div>
            <div className="w-2.5 h-2.5 rounded-full bg-green-500/80"></div>
          </div>
          <span className="text-xs text-slate-400">askchad.io</span>
        </div>
        <div className="flex gap-3 items-start">
          <div className="w-8 h-8 rounded bg-emerald-500/90 flex-shrink-0 flex items-center justify-center text-slate-900 font-bold text-sm">
            C
          </div>
          <div className="flex-1">
            <div className="bg-slate-700/50 backdrop-blur rounded-lg p-3">
              <p className="text-slate-200 text-sm min-h-[1.5rem]">
                {currentText}
                {isTyping && (
                  <span className="ml-0.5 inline-block w-0.5 h-4 bg-emerald-500 animate-pulse" />
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SuccessCardProps {
  onReset: () => void;
}

const SuccessCard: React.FC<SuccessCardProps> = ({ onReset }) => (
  <div className="text-center">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-emerald-500 text-slate-900 mb-4">
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
    <h3 className="text-xl font-semibold text-slate-100 mb-4">
      Check Your Email
    </h3>
    <p className="text-slate-300 mb-6">
      We've sent a link to your email (check your Spam folder). Click it to start your journey with CHAD.
    </p>
    <button
      onClick={onReset}
      className="text-emerald-500 hover:text-emerald-400"
    >
      Use a different email
    </button>
  </div>
);

// Testimonial component
interface TestimonialProps {
  name: string;
  role: string;
  initial: string;
  quote: string;
}

const Testimonial: React.FC<TestimonialProps> = ({ name, role, initial, quote }) => (
  <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6">
    <div className="flex items-center mb-4">
      <div className="w-12 h-12 bg-emerald-500 rounded-full flex items-center justify-center text-slate-900 font-bold text-xl mr-4">
        {initial}
      </div>
      <div>
        <h4 className="text-lg font-semibold text-slate-100">{name}</h4>
        <p className="text-sm text-slate-400">{role}</p>
      </div>
      <div className="ml-auto flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <svg key={star} className="w-5 h-5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
          </svg>
        ))}
      </div>
    </div>
    <p className="text-slate-300 italic">"{quote}"</p>
  </div>
);

// Feature component
interface FeatureProps {
  title: string;
  description: string;
  icon: React.ReactNode;
}

const Feature: React.FC<FeatureProps> = ({ title, description, icon }) => (
  <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-6">
    <div className="w-12 h-12 bg-emerald-500/20 rounded-lg flex items-center justify-center mb-4">
      {icon}
    </div>
    <h3 className="text-xl font-semibold text-slate-100 mb-2">{title}</h3>
    <p className="text-slate-300">{description}</p>
  </div>
);

const OptimizedLoginForm: React.FC<LoginFormProps> = ({ onSubmit, isLoading }) => {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [token, setToken] = useState('');
  const widgetId = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Turnstile setup code
  useEffect(() => {    
    const loadTurnstile = () => {
      if (containerRef.current && window.turnstile) {
        widgetId.current = window.turnstile.render(containerRef.current, {
          sitekey: '0x4AAAAAAA4OGMUHD1Kc7Rta',
          theme: 'dark',
          callback: function(token: string) {
            setToken(token);
          },
        });
      }
    };

    if (window.turnstile) {
      loadTurnstile();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.onload = loadTurnstile;

    document.head.appendChild(script);

    return () => {
      if (script.parentElement) {
        script.parentElement.removeChild(script);
      }
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) {
      alert('Please complete the CAPTCHA');
      return;
    }

    try {
      await onSubmit(email, token);
      // Instead of immediately setting sent to true, show a "Check your email" message
      setSent(true);
    } catch (error: any) {
      if (window.turnstile && widgetId.current) {
        window.turnstile.reset(widgetId.current);
      }
      setToken('');
      // Show error to user
      alert(error.message || 'Failed to send verification email');
    }
  };

  const handleReset = () => {
    setSent(false);
    setEmail('');
    setToken('');
    if (window.turnstile && widgetId.current) {
      window.turnstile.reset(widgetId.current);
      const oldWidget = containerRef.current?.querySelector('iframe');
      if (oldWidget) {
        oldWidget.remove();
      }
      setTimeout(() => {
        if (containerRef.current) {
          widgetId.current = window.turnstile.render(containerRef.current, {
            sitekey: '0x4AAAAAAA4OGMUHD1Kc7Rta',
            theme: 'dark',
            callback: function(newToken: string) {
              setToken(newToken);
            },
          });
        }
      }, 100);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-emerald-600/10 via-slate-900/50 to-slate-900 -z-10" />
      
      {/* Two-column layout container */}
      <div className="flex flex-col lg:flex-row min-h-screen">
        {/* Mobile signup form - only visible on small screens */}
        <div className="lg:hidden w-full mb-8">
          <div className="bg-slate-800/70 backdrop-blur border border-slate-700 rounded-2xl shadow-xl p-6 max-w-md mx-auto">
            {sent ? (
              <SuccessCard onReset={handleReset} />
            ) : (
              <>
                <h2 className="text-2xl font-bold text-slate-100 text-center mb-2">
                  Login to Chad
                </h2>
                <p className="text-slate-400 text-center mb-6">
                  Sign up free. No password needed.
                </p>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email-mobile" className="sr-only">Email Address</label>
                    <input
                      type="email"
                      id="email-mobile"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                  <div className="flex justify-center">
                    <div ref={containerRef} />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading || !token || !email}
                    className="w-full py-3 px-4 bg-emerald-500 text-slate-900 rounded-lg hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center gap-2">
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Verifying...
                      </span>
                    ) : (
                      'Get Started Free'
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
        
        {/* Left column - Marketing content (scrollable) */}
        <div className="w-full lg:w-2/3 overflow-y-auto">
          <div className="px-4 sm:px-6 lg:px-4 py-8 lg:py-12">
            {/* Mobile logo - only visible on small screens */}
            <div className="flex justify-center lg:hidden mb-6">
              <img src="/android-chrome-192x192.png" alt="CHAD Logo" className="h-16 w-16" />
            </div>
            
            {/* Hero Section */}
            <div className="max-w-4xl mx-auto mb-16">
              <div className="text-center lg:text-left">
                {/* Desktop logo - only visible on large screens */}
                <div className="hidden lg:block mb-8">
                  <img src="/android-chrome-192x192.png" alt="CHAD Logo" className="h-16 w-16 mx-auto lg:mx-0 mb-8" />
                  <h1 className="text-4xl font-bold tracking-tight text-slate-100 sm:text-5xl xl:text-6xl mb-6">
                  $20 to Own Your Life Forever<br/><span className="text-emerald-500">No Bullshit!</span>
                  </h1>
                  <p className="mx-auto lg:mx-0 max-w-2xl text-xl text-slate-300 mb-8">
                  Make Your Wife Want You Again (or stay stuck)
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-4 justify-center lg:justify-start mb-8">
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Free Tier Available</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>No Credit Card Required</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>End-to-End Encryption</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Discreet Billing</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>Cheap Yearly Subscription</span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-300">
                      <svg className="w-5 h-5 text-emerald-500" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                      <span>100% Private</span>
                    </div>
                  </div>
                  <div className="mb-4 max-w-2xl mx-auto lg:mx-0">
                    <h3 className="text-xl font-semibold text-slate-100 mb-4 text-center lg:text-left">
                      Ask CHAD Anything
                    </h3>
                    <TypewriterDemo />
                  </div>
                </div>
              </div>                            
              <div className="lg:col-span-5 mt-8 lg:mt-0 lg:hidden">
                {/* Empty div for layout purposes on large screens */}
              </div>
            </div>
          </div>
          
          {/* What You Get Section - Sales Letter Style */}
          <div className="py-8 border-t border-slate-800">
            <div className="text-center mb-8">
              <h2 className="text-3xl font-bold text-slate-100 mb-4">The <span className="text-emerald-500">Only</span> Straight Shooting AI for Men</h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              If you want to talk about your feelings or keep doing the dishes,<br/>go ahead, ask the other AIs for help.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-emerald-500 mb-4">The Hard Truth No One Else Will Tell You</h3>
                
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-slate-300 mb-4">
                  Most guys are stuck—weak marriages, flabby bodies, dead-end careers. They've lost their edge and don't know how to get it back. Sound familiar? You're not alone, but you're <span className="text-emerald-500">running out of time to fix it.</span>
                  </p>
                  
                  <p className="text-slate-300 mb-4">
                  I've seen it all—men who've let life beat them down, who've forgotten what it means to lead, to fight, to win. Hell, maybe I'm talking to you right now. But here's the truth: <span className="text-emerald-500">it doesn't have to be this way.</span>
                  </p>
                  
                  <p className="text-slate-300">
                  It's no wonder your wife or gf isn't in to you, why should she be? Fortunately for you, CHAD was built to turn you into the man you're supposed to be. He'll give you the straight talk and guidance to dominate at home, in the gym, and in the game of life. No fluff, no excuses—<span className="text-emerald-500">just results.</span> Does that sound more like a guy she'd want to get with? Yeah, I thought so.
                  </p>
                </div>
              </div>
              
              <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-8 mb-8">
                <h3 className="text-2xl font-bold text-emerald-500 mb-4">Tired of Feeling Like a Loser?<br/>This is the Blueprint to Fix Your Shit—Start Today!</h3>
                
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-slate-300 mb-4">
                Choose your path: Free limited access with ads if you're just curious, or choose a <a href="https://askchad.io/alpha-upgrade"><span className="text-emerald-500"><u>$20 per year</u></span></a> for <a href="https://askchad.io/alpha-upgrade"><span className="text-emerald-500"><u>ALPHA</u></span></a> status—unlimited, instant access to everything. Trade what you'd spend on coffee for a week for a complete sex life overhaul. What's your excuse?
                  </p>
                  
                  <p className="text-slate-300 mb-4">
                  Every day you wait, you're slipping further behind. Heck, maybe your wife's eyes are already starting to wander... <span className="text-emerald-500">Real men take action.</span> Enter your email to join now—become the Alpha you were born to be, or stay on the sidelines and hope for the best. Your choice.
                  </p>
                  
                  <p className="text-slate-300">
                    No more wasting time on trial and error. No more confusion about what to do next. Just <span className="text-emerald-500">clear, actionable guidance</span> that cuts through the bullshit and gets you moving in the right direction—<span className="text-emerald-500">fast.</span>
                  </p>
                </div>
              </div>
              
              <div className="bg-slate-800/50 backdrop-blur border border-slate-700 rounded-xl p-8">
                <h3 className="text-2xl font-bold text-emerald-500 mb-4">Ask Anything, Anytime—Zero Judgment, Total Privacy</h3>
                
                <div className="prose prose-lg prose-invert max-w-none">
                  <p className="text-slate-300 mb-4">
                    Look, man, you've got questions burning a hole in your head—shit about your marriage, your bedroom game, your insecurities, or the fears that keep you up at night. Stuff you'd never dare ask anyone, not even your closest buddy. I get it... But that shame's holding you back.
                  </p>
                  
                  <p className="text-slate-300 mb-4">
                  With CHAD, you can spill your guts on anything—<span className="text-emerald-500">ANYTHING</span>—and he won't bat an eye. No judgment, no bullshit. Every word is locked down with end-to-end encryption. <span className="text-emerald-500">Completely confidential.</span> Nobody but you will ever know what the hell you asked. Period.
                  </p>
                  
                  <p className="text-slate-300">
                  This ain't just about keeping secrets—it's about breaking free. When you're brutally honest, you get the real answers you need, not the watered-down crap you're okay asking for. That's where your life changes. <span className="text-emerald-500">That's where you stop hiding and start winning.</span>
                  </p>

                  <p className="text-slate-300">
                  Ready to finally say what's on your mind? Join CHAD now—free with limits or $20/year for <a href="https://askchad.io/alpha-upgrade"><span className="text-emerald-500"><u>FULL ALPHA</u></span></a> access. Don't pussyfoot around. Let's fix your shit today.
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Testimonials Section */}
          <div className="py-8 border-t border-slate-800">
            <div className="text-center mb-8 px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-slate-100 mb-4">Real Results from Real Users</h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto">
                Don't just take our word for it. See what other guys are saying about CHAD.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 gap-8 px-4 sm:px-6 lg:px-8">
              <Testimonial 
                name="Mike T." 
                role="Project Manager, 32" 
                initial="M" 
                quote="My bedroom was dead. Despite trying everything I could think of to spice things up, it wasn't until I explained my situation to CHAD that I finally made progress. 6 months later and my sex life took a complete 180. Oh, gotta go, wife needs me again :)"
              />
              
              <Testimonial 
                name="James K." 
                role="Business Owner, 41" 
                initial="J" 
                quote="My marriage falling apart when I found CHAD. The direct advice on leading and becoming someone my wife was attracted to saved my relationship. Worth every penny and then some."
              />
              
              <Testimonial 
                name="Tom P." 
                role="Marketing Director, 36" 
                initial="T" 
                quote="I thought things were fine at home, but since I started following CHAD's advice I now realize they definitely were not. I never knew my wife could be so wild in bed! I'm just glad CHAD fixed my shit before she ended up doing it with someone else!"
              />
              
              <Testimonial 
                name="David R." 
                role="Software Engineer, 29" 
                initial="D" 
                quote="CHAD is the real deal. None of this is in relationship or self-help books... Anything published is too watered down and 'safe' to actually be effective. Your relationshp can be the envy of everyone else, just askCHAD."
              />
            </div>
          </div>
          
          {/* Stats Section */}
          <div className="py-8 border-t border-slate-800">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-100 mb-1">100K+</div>
                <div className="text-sm text-slate-400">Active Users</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-100 mb-1">1M+</div>
                <div className="text-sm text-slate-400">Messages Sent</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-100 mb-1">4.9/5</div>
                <div className="text-sm text-slate-400">User Rating</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-slate-100 mb-1">24/7</div>
                <div className="text-sm text-slate-400">Availability</div>
              </div>
            </div>
          </div>
          
          {/* Final CTA Section */}
          <div className="py-8 border-t border-slate-800">
            <div className="text-center px-4 sm:px-6 lg:px-8">
              <h2 className="text-3xl font-bold text-slate-100 mb-4">Not sure? Test the free version first</h2>
              <p className="text-xl text-slate-300 max-w-3xl mx-auto mb-8">
               Once you're serious, <a href="https://askchad.io/alpha-upgrade"><span className="text-emerald-500"><u>FULL ALPHA</u></span></a> is the only way to play.
              </p>
              <p><center><a href="https://theresanaiforthat.com/ai/askchad-io/?ref=featured&v=5844834" target="_blank" rel="nofollow"><img width="300" src="https://media.theresanaiforthat.com/featured-on-taaft.png?width=600"></img></a></center></p>
              <a href="#signup" className="inline-block py-3 px-8 bg-emerald-500 text-slate-900 rounded-lg hover:bg-emerald-400 font-medium transition-colors lg:hidden">
                Get Started Now
              </a>
            </div>
            
            {/* Footer */}
            <footer className="max-w-4xl mx-auto pt-8 border-t border-slate-800 px-4 sm:px-6 lg:px-8">
              <div className="text-center text-sm text-slate-400">
                <div className="space-x-4 mb-4">
                  <a href="/privacy" className="text-emerald-400 hover:text-emerald-300">Privacy Policy</a>
                  <span className="text-slate-600">•</span>
                  <a href="/terms" className="text-emerald-400 hover:text-emerald-300">Terms of Service</a>
                  <span className="text-slate-600">•</span>
                  <a href="/aup" className="text-emerald-400 hover:text-emerald-300">Acceptable Use</a>
                  <span className="text-slate-600">•</span>
                  <a href="mailto:email@lincolnfreedomgroup.com" className="text-emerald-400 hover:text-emerald-300">Contact Us</a>
                </div>
                <p>© 2025 Lincoln Freedom Group, LLC. All rights reserved.</p>
              </div>
            </footer>
          </div>
        </div>
        
        {/* Right column - Signup form (fixed position) */}
        <div className="w-full lg:w-1/3" id="signup">
          {/* Mobile: Sticky at top, Desktop: Fixed position */}
          <div className="lg:sticky lg:top-0 lg:h-screen lg:flex lg:flex-col lg:justify-center p-4 sm:p-6 lg:p-4">
            <div className="bg-slate-800/70 backdrop-blur border border-slate-700 rounded-2xl shadow-xl p-6 lg:p-8 max-w-md mx-auto">
              {sent ? (
                <SuccessCard onReset={handleReset} />
              ) : (
                <>
                  <h2 className="text-2xl font-bold text-slate-100 text-center mb-2">
                    Login to CHAD
                  </h2>
                  <p className="text-slate-400 text-center mb-6">
                    Sign up free.<br/>No password needed.
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div>
                      <label htmlFor="email" className="sr-only">Email Address</label>
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-3 rounded-lg bg-slate-700/50 border border-slate-600 text-slate-100 placeholder-slate-400 focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                        placeholder="Enter your email"
                        required
                      />
                    </div>
                    <div className="flex justify-center">
                      <div ref={containerRef} />
                    </div>

                    <button
                      type="submit"
                      disabled={isLoading || !token || !email}
                      className="w-full py-3 px-4 bg-emerald-500 text-slate-900 rounded-lg hover:bg-emerald-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 focus:ring-offset-slate-800 disabled:opacity-50 disabled:cursor-not-allowed font-medium transition-colors"
                    >
                      {isLoading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Verifying...
                        </span>
                      ) : (
                        'Get Started Free'
                      )}
                    </button>
                  </form>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OptimizedLoginForm;
